import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import axios from 'axios';
import { Form, Input, Modal, Radio, message } from 'antd';
import { useAuth } from '../../infrastructure/auth0/useAuth';
import { useInvitationsData } from '../../hooks/data/useInvitationsData';

type Props = {
  isVisible: boolean;
  onClose: () => void;
};

const CreateInvitationModal = ({ isVisible, onClose }: Props): ReactNode => {
  const { t } = useTranslation();
  const [form] = Form.useForm<{ mail: string; role: 'member' | 'admin' }>();
  const { currentUser } = useAuth();
  const { createInvitation } = useInvitationsData();

  const handleClickCreateInvitation = async (): Promise<void> => {
    try {
      const submitValues = await form.validateFields();

      onClose();

      const messageKey = 'create-invitation-message';
      message.loading({
        key: messageKey,
        content: t('message.invitingMember'),
        duration: 0,
      });

      createInvitation({
        inviterName: currentUser?.userName || '',
        email: submitValues.mail,
        is_admin: submitValues.role === 'admin',
      })
        .then((): void => {
          message.success({
            key: messageKey,
            content: t('message.memberInvited'),
            duration: 4,
          });
        })
        .catch((error: unknown): void => {
          let msg = t('message.failedToInviteMember');
          if (axios.isAxiosError(error) && error.response?.data?.message) {
            msg += ` (${error.response.data.message})`;
          }
          message.error({
            key: messageKey,
            content: msg,
            duration: 4,
          });
        });
    } catch {
      // バリデーションエラー時は何もしない
    }
  };

  return (
    <Modal
      title={t('inviteMember')}
      cancelText={t('cancel')}
      okText={t('submit')}
      visible={isVisible}
      onCancel={onClose}
      onOk={handleClickCreateInvitation}
      centered
    >
      <StyledForm
        name="add-member"
        form={form}
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 12 }}
        initialValues={{ remember: true, role: 'member' }}
        autoComplete="off"
      >
        <Form.Item
          label={t('emailAddress')}
          name="mail"
          rules={[
            { required: true, message: t('message.emailRequired') },
            {
              type: 'email',
              message: t('message.emailInvalid'),
            },
          ]}
        >
          <Input size="small" />
        </Form.Item>
        <Form.Item label={t('role')} name="role">
          <Radio.Group>
            <Radio value="member">{t('roleMember')}</Radio>
            <Radio value="admin">{t('roleAdmin')}</Radio>
          </Radio.Group>
        </Form.Item>
      </StyledForm>
    </Modal>
  );
};

const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 12px;
  }
`;

export { CreateInvitationModal };
